import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useExitLock } from "../hooks/useExitLock";
import { useCart } from "../context/CartContext";
import { scrollToId } from "../lib/lenis";
import { beep } from "../lib/sound";

const JP = {
  Rings: "指輪",
  "Signature Clips": "ヘアクリップ",
  Chains: "チェーン",
  Accessories: "アクセサリー",
  Apparel: "服",
};
const SMALL = 15;
const SEL = 36;
const GAP = 3;

function Ring({ product, selected, onSelect, onOpen }) {
  const size = selected ? SEL : SMALL;
  return (
    <motion.button
      data-testid={`spine-${product.id}`}
      onClick={() => (selected ? onOpen(product) : onSelect(product.id))}
      title={selected ? "Open artifact file" : undefined}
      animate={{ width: `${size}vh`, height: `${size}vh`, opacity: selected ? 1 : 0.5, y: selected ? -10 : 0 }}
      whileHover={selected ? {} : { opacity: 0.9, y: -6 }}
      transition={{ type: "spring", stiffness: 200, damping: 26 }}
      className={`relative shrink-0 ${selected ? "cursor-pointer" : ""}`}
      aria-label={product.name}
    >
      <motion.img
        src={product.image}
        alt={product.name}
        loading="lazy"
        draggable={false}
        animate={selected ? { y: [0, -8, 0] } : { y: 0 }}
        transition={selected ? { duration: 4.5, repeat: Infinity, ease: "easeInOut" } : {}}
        className={`h-full w-full object-contain ${selected ? "" : "grayscale-[0.35]"}`}
      />
      {selected && (
        <div className="pointer-events-none absolute inset-x-[15%] -bottom-3 h-6 rounded-full bg-[radial-gradient(ellipse,rgba(190,235,255,0.35),transparent_70%)] blur-md" />
      )}
    </motion.button>
  );
}

export default function Catalog({ products }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const openDetail = (p) => {
    beep(900, 0.07, 0.05);
    navigate(`/product/${p.id}`);
  };
  const categories = useMemo(() => ["ALL", ...new Set(products.map((p) => p.category))], [products]);
  const [filter, setFilter] = useState("ALL");
  const list = useMemo(
    () => (filter === "ALL" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );
  const [selectedId, setSelectedId] = useState(null);
  const [vh, setVh] = useState(() => window.innerHeight / 100);

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight / 100);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!list.length) return;
    if (!list.some((p) => p.id === selectedId)) {
      setSelectedId((list.find((p) => p.signature) || list[0]).id);
    }
  }, [list, selectedId]);

  const index = Math.max(0, list.findIndex((p) => p.id === selectedId));
  const selected = list[index];
  const offset = (index * (SMALL + GAP) + SEL / 2) * vh;

  const step = (dir) => {
    if (!list.length) return;
    beep(dir > 0 ? 620 : 480, 0.06);
    setSelectedId(list[(index + dir + list.length) % list.length].id);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // the catalog rides up over the last screen of the tunnel and snaps in as you step through the door
  const sectionRef = useRef(null);
  const { scrollYProgress: arrive } = useScroll({ target: sectionRef, offset: ["start end", "start start"] });
  const opacity = useTransform(arrive, [0.78, 0.9], [0, 1]);
  const [live, setLive] = useState(false);
  useMotionValueEvent(arrive, "change", (v) => setLive(v > 0.84));

  useExitLock(sectionRef);

  return (
    <motion.section
      ref={sectionRef}
      style={{ opacity }}
      id="platform"
      data-testid="platform-section"
      className={`relative z-10 -mt-[100vh] flex h-screen flex-col overflow-hidden bg-black pt-16 ${live ? "" : "pointer-events-none"}`}
    >
      <header className="flex items-center justify-between px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-white/70 sm:px-10">
        <span className="w-24" aria-hidden="true" />
        <div className="text-center leading-tight">
          <p className="text-sm font-bold tracking-[0.3em] text-white">GUMZ 商品</p>
          <p className="text-[9px] text-white/50">STUDIOS CATALOG</p>
        </div>
        <p className="hidden sm:block">EST. 2026 // 創業</p>
      </header>

      <div className="flex flex-wrap justify-center gap-1.5 px-5" data-testid="catalog-filters">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => {
              beep(520, 0.05);
              setFilter(c);
            }}
            className={`btn-y2k px-3 py-1 text-[9px] tracking-[0.2em] ${filter === c ? "brightness-90 shadow-inner" : ""}`}
          >
            {c === "ALL" ? "ALL / すべて" : `${c.toUpperCase()} / ${JP[c] || ""}`}
          </button>
        ))}
      </div>

      <div className="relative min-h-0 flex-1" data-testid="catalog-shelf">
        <motion.div
          animate={{ x: -offset }}
          transition={{ type: "spring", stiffness: 150, damping: 26 }}
          className="absolute left-1/2 top-[42%] flex -translate-y-1/2 items-center"
          style={{ gap: `${GAP}vh` }}
        >
          {list.map((p) => (
            <Ring key={p.id} product={p} selected={p.id === selectedId} onSelect={setSelectedId}
              onOpen={openDetail} />
          ))}
        </motion.div>
        <button
          data-testid="catalog-prev-button"
          onClick={() => step(-1)}
          aria-label="Previous artifact"
          className="btn-y2k absolute left-6 top-1/2 -translate-y-1/2 p-2 sm:left-10"
        >
          <ArrowLeft size={14} />
        </button>
        <button
          data-testid="catalog-next-button"
          onClick={() => step(1)}
          aria-label="Next artifact"
          className="btn-y2k absolute right-6 top-1/2 -translate-y-1/2 p-2 sm:right-10"
        >
          <ArrowRight size={14} />
        </button>
      </div>

      {selected && (
        <motion.div
          key={selected.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="catalog-detail"
          className="px-6 pb-6 text-center"
        >
          <h3 data-testid="catalog-selected-name" className="font-display text-xl font-bold text-white sm:text-2xl">
            {selected.name}
          </h3>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-danger">
            {selected.category.toUpperCase()} / {JP[selected.category]} / {selected.anomaly_class}
          </p>
          <p className="mx-auto mt-2 max-w-md font-mono text-xs leading-relaxed text-white/60">{selected.description}</p>
          <div className="mt-3 flex items-center justify-center gap-4">
            <span data-testid="catalog-selected-price" className="font-mono text-base font-bold text-white">
              ${selected.price}
            </span>
            <button
              data-testid={`acquire-button-spotlight-${selected.id}`}
              onClick={() => {
                addItem(selected);
                beep(1320, 0.08, 0.05);
                toast.success("ARTIFACT SECURED", { description: selected.name });
              }}
              className="btn-y2k flex items-center gap-2 px-5 py-2 text-[10px] tracking-[0.25em]"
            >
              <Zap size={12} /> ACQUIRE
            </button>
          </div>
        </motion.div>
      )}

      <footer className="flex items-center justify-between border-t border-white/10 px-6 py-2 font-mono text-[9px] tracking-[0.25em] text-white/40 sm:px-10">
        <span>© ALL RIGHTS RESERVED.</span>
        <span>IG / TW</span>
      </footer>
    </motion.section>
  );
}
